import { ActivityNote } from "../models/activity-notes.model";
import { ApplicationCache } from "../models/application-cache.model";
import {
  activityNoteBody,
  isEmptyScheduleCreatedActivity,
} from "./schedule-activity-note.util";

/**
 * Legacy activity types that were previously mis-tagged as nurse notes.
 * Kept for reference only; nurse-note detection uses the `isNurseNote` flag.
 */
export const PROCEDURE_NURSE_NOTE_ACTIVITY_TYPES = new Set(
  [
    "Cancel Procedure",
    "Pre-Procedure Action",
    "Prior Auth",
    "Prior Auth - Denial",
    "Complete Procedure",
    "Pre-Procedure Assessment Completed",
    "Patient Checked In",
  ].map((t) => t.toLowerCase())
);

/** Coerce API / socket values (`true`, `"true"`, `1`, `"1"`) into a boolean. */
export function coerceIsNurseNoteFlag(value: unknown): boolean {
  if (value === true || value === 1) {
    return true;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
  }
  return false;
}

function readNurseNoteFlagFromRecord(rec: Record<string, unknown>): boolean {
  const keys = [
    "isNurseNote",
    "is_nurse_note",
    "isNurse",
    "is_nurse",
    "nurseNote",
    "nurse_note",
  ];
  for (const key of keys) {
    if (key in rec && coerceIsNurseNoteFlag(rec[key])) {
      return true;
    }
  }
  const nested = rec["activity"];
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return readNurseNoteFlagFromRecord(nested as Record<string, unknown>);
  }
  return false;
}

/**
 * True when a patient-activity row is a procedure-room nurse note (`isNurseNote` flag only).
 */
export function activityRecordIsNurseNote(
  record: ActivityNote | Record<string, unknown> | null | undefined
): boolean {
  if (!record || typeof record !== "object") {
    return false;
  }
  return readNurseNoteFlagFromRecord(record as Record<string, unknown>);
}

/** Normalize one clinic activity row (`activity_type` → `activityType`). */
export function normalizeActivityNoteRecord(
  note: ActivityNote | Record<string, unknown>
): ActivityNote {
  if (!note || typeof note !== "object") {
    return note as ActivityNote;
  }
  const rec = note as Record<string, unknown>;
  const activityType = String(
    rec["activityType"] ?? rec["activity_type"] ?? ""
  ).trim();
  const encounterId = String(
    rec["encounterId"] ?? rec["encounter_id"] ?? ""
  ).trim();
  const selectedDate = String(
    rec["selectedDate"] ?? rec["selected_date"] ?? ""
  ).trim();
  return {
    ...(note as ActivityNote),
    activityType,
    ...(encounterId ? { encounterId } : {}),
    ...(selectedDate ? { selectedDate } : {}),
  };
}

/** Unwrap activities array from common API envelope shapes. */
export function normalizeActivityNotesList(response: unknown): ActivityNote[] {
  const unwrap = (): ActivityNote[] => {
    if (Array.isArray(response)) {
      return response as ActivityNote[];
    }
    if (!response || typeof response !== "object") {
      return [];
    }
    const rec = response as Record<string, unknown>;
    for (const key of ["data", "activities", "items", "results"]) {
      const value = rec[key];
      if (Array.isArray(value)) {
        return value as ActivityNote[];
      }
    }
    return [];
  };
  return filterRedundantEmptyScheduleCreatedActivities(
    unwrap().map((note) => normalizeActivityNoteRecord(note))
  );
}

/** Drop timestamp-only Schedule Created stubs when a detailed row exists for the encounter. */
export function filterRedundantEmptyScheduleCreatedActivities(
  notes: ActivityNote[]
): ActivityNote[] {
  const encounterIdsWithDetailed = new Set<string>();
  for (const note of notes) {
    if (isEmptyScheduleCreatedActivity(note)) {
      continue;
    }
    const type = String(note.activityType || "").trim();
    if (type !== "Schedule Created") {
      continue;
    }
    const eid = String(note.encounterId || "").trim();
    if (eid && activityNoteBody(note)) {
      encounterIdsWithDetailed.add(eid);
    }
  }
  if (!encounterIdsWithDetailed.size) {
    return notes;
  }
  return notes.filter((note) => {
    const eid = String(note.encounterId || "").trim();
    if (!eid || !encounterIdsWithDetailed.has(eid)) {
      return true;
    }
    return !isEmptyScheduleCreatedActivity(note, eid);
  });
}

/** Clinic procedure activity row (`activityType: Referral Created`). */
export function isReferralCreatedActivityType(activityType: unknown): boolean {
  const at = String(activityType ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  return at === "referral created";
}

/** Clinic procedure activity rows that should use the note timeline icon. */
export function activityTypeUsesNoteIcon(activityType: unknown): boolean {
  if (isReferralCreatedActivityType(activityType)) {
    return false;
  }
  const at = String(activityType ?? "")
    .trim()
    .toLowerCase();
  if (!at) {
    return false;
  }
  if (at === "manual" || at === "memo" || /\bnote\b/.test(at)) {
    return true;
  }
  return at.startsWith("coverage -") || at.startsWith("coverage-");
}

/**
 * @deprecated API scopes activity lists via `?isNurseNote=` and row `displayMode`; no client filter.
 */
export function filterActivityNotesByNurseFlag(
  notes: ActivityNote[],
  isNurseNote?: boolean
): ActivityNote[] {
  if (isNurseNote === true) {
    return notes.filter((note) => activityRecordIsNurseNote(note));
  }
  if (isNurseNote === false) {
    return notes.filter((note) => !activityRecordIsNurseNote(note));
  }
  return notes;
}

/** Merge procedure-room activity rows from both caches (legacy + nurse-note buckets). */
export function mergeProcedureRoomActivityNotesForLookup(
  cache: ApplicationCache | null | undefined
): ActivityNote[] {
  if (!cache) {
    return [];
  }
  const byId = new Map<string, ActivityNote>();
  for (const note of [
    ...(cache.procedureRoomActivityNotes || []),
    ...(cache.procedureRoomNurseActivityNotes || []),
  ]) {
    if (note?.id) {
      byId.set(note.id, note);
    }
  }
  return [...byId.values()];
}
